import React, { useState } from "react";
import { Button, Modal, Tooltip } from "antd";
import { TranslationOutlined, CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useModal } from "../../hooks";
import theme from "../../../config/theme";

interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: LanguageOption[] = [
  { code: "en", name: "English", nativeName: "English", flag: "🇺🇸" },
  { code: "zh", name: "Traditional Chinese", nativeName: "中文", flag: "🇹🇼" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
];

// Modern language selection modal with card-based UI
const SetLangButton: React.FC = () => {
  const { i18n } = useTranslation();
  const modal = useModal();
  const [hoveredLang, setHoveredLang] = useState<string | null>(null);

  const handleLanguageSelect = (langCode: string) => {
    localStorage.setItem("user_pref:lang", langCode);
    i18n.changeLanguage(langCode);
    modal.close();
  };

  // Get current language display
  const getCurrentLanguage = () => {
    const current = languages.find((lang) => lang.code === i18n.language);
    return current || languages[0];
  };

  const currentLang = getCurrentLanguage();

  return (
    <div>
      <Tooltip title="Change Language">
        <Button
          icon={<TranslationOutlined style={{ fontSize: theme.typography.fontSize.lg }} />}
          onClick={modal.open}
          size="large"
          style={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            color: theme.colors.text.inverse,
            height: theme.layout.button.heightLarge,
            minWidth: "100px",
            fontWeight: theme.typography.fontWeight.medium,
            borderRadius: theme.borderRadius.lg,
            transition: theme.transitions.fast,
          }}
          className="header-action-button"
          aria-label="Open language selection"
        >
          {currentLang.nativeName}
        </Button>
      </Tooltip>

      <Modal
        title={
          <div style={{ textAlign: "center" }}>
            <TranslationOutlined
              style={{
                fontSize: theme.typography.fontSize["3xl"],
                color: theme.colors.primary.main,
                marginBottom: theme.spacing.sm,
              }}
            />
            <h2
              style={{
                margin: 0,
                fontSize: theme.typography.fontSize["2xl"],
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.text.primary,
              }}
            >
              Choose Your Language
            </h2>
            <p
              style={{
                margin: `${theme.spacing.xs} 0 0 0`,
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.secondary,
              }}
            >
              選擇您的語言 • Elige tu idioma
            </p>
          </div>
        }
        open={modal.isOpen}
        onCancel={modal.close}
        footer={null}
        centered
        width={480}
        styles={{
          body: {
            padding: `${theme.spacing.lg} ${theme.spacing.xl} ${theme.spacing.xl}`,
          },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing.md,
          }}
        >
          {languages.map((lang) => {
            const isSelected = i18n.language === lang.code;
            const isHovered = hoveredLang === lang.code;

            return (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                onMouseEnter={() => setHoveredLang(lang.code)}
                onMouseLeave={() => setHoveredLang(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleLanguageSelect(lang.code);
                  }
                }}
                tabIndex={0}
                aria-label={`Select ${lang.name}`}
                aria-pressed={isSelected}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
                  border: isSelected
                    ? `2px solid ${theme.colors.primary.main}`
                    : `2px solid ${theme.colors.neutral.gray300}`,
                  borderRadius: theme.borderRadius.xl,
                  background: isSelected
                    ? theme.colors.primary.lightest
                    : isHovered
                      ? theme.colors.neutral.gray100
                      : theme.colors.neutral.white,
                  cursor: "pointer",
                  transition: `all ${theme.transitions.fast}`,
                  boxShadow: isHovered || isSelected ? theme.shadows.md : theme.shadows.sm,
                  transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                  outline: "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.md }}>
                  <span
                    style={{
                      fontSize: theme.typography.fontSize["3xl"],
                      lineHeight: 1,
                    }}
                  >
                    {lang.flag}
                  </span>
                  <div style={{ textAlign: "left" }}>
                    <div
                      style={{
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.colors.text.primary,
                        marginBottom: "2px",
                      }}
                    >
                      {lang.nativeName}
                    </div>
                    <div
                      style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.text.secondary,
                      }}
                    >
                      {lang.name}
                    </div>
                  </div>
                </div>

                {isSelected && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                      borderRadius: theme.borderRadius.full,
                      background: theme.colors.primary.main,
                      color: theme.colors.text.inverse,
                    }}
                  >
                    <CheckOutlined style={{ fontSize: theme.typography.fontSize.base }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div
          style={{
            marginTop: theme.spacing.lg,
            padding: theme.spacing.md,
            background: theme.colors.neutral.gray100,
            borderRadius: theme.borderRadius.lg,
            textAlign: "center",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize.xs,
              color: theme.colors.text.tertiary,
            }}
          >
            Your language preference will be saved automatically
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default SetLangButton;
